import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReasonCaptureState } from "./useReasonCapture";
import { TEXT_MAX } from "./validation";

export const FEEDBACK_HELPER = "Vira um feedback na Melhoria contínua (não dá para editar depois) e fica no histórico do pacote com seu nome e a data.";

export type ReasonPickerProps = { capture: ReasonCaptureState; label?: string };

const grid = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 2, rowGap: 0.25 };

/** Motivos em 2 colunas (rádios; checkboxes no atrito) + "Detalhe" obrigatório quando o motivo pede texto. */
export default function ReasonPicker({ capture, label = "Motivo" }: ReasonPickerProps) {
  const { ordered, value, toggle, multiple, text, setText, chosen, textRequired, textError } = capture;
  const labelId = `reason-label-${capture.moment}`;
  const showError = textRequired && text.length > 0 && Boolean(textError);
  const helper = textRequired
    ? `Obrigatório para “${chosen.filter((r) => r.requiresText).map((r) => r.label).join("”, “")}”. ${showError ? textError : ""} ${FEEDBACK_HELPER}`
    : FEEDBACK_HELPER;

  return (
    <Box>
      <Typography id={labelId} sx={{ fontSize: 14, fontWeight: 500, mb: 0.5 }}>
        {label}{" "}
        <Box component="span" aria-hidden sx={{ color: "error.main" }}>
          *
        </Box>
      </Typography>
      {capture.loading ? (
        <Box sx={grid} data-testid="reasons-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={32} />
          ))}
        </Box>
      ) : capture.error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={capture.retry}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar os motivos.
        </Alert>
      ) : (
        <Box role={multiple ? "group" : "radiogroup"} aria-labelledby={labelId} aria-required="true" sx={grid}>
          {ordered.map((reason) => {
            const checked = value.includes(reason.id);
            const control = multiple ? (
              <Checkbox checked={checked} onChange={() => toggle(reason.id)} value={String(reason.id)} />
            ) : (
              <Radio checked={checked} onChange={() => toggle(reason.id)} value={String(reason.id)} name={`reason-${capture.moment}`} />
            );
            return (
              <FormControlLabel
                key={reason.id}
                control={control}
                sx={{ alignItems: "flex-start", "& .MuiFormControlLabel-label": { pt: "9px", fontSize: 14 } }}
                label={
                  <>
                    {reason.label}
                    {reason.requiresText && (
                      <Box component="span" sx={{ color: "text.secondary", fontSize: 12, ml: 0.5 }}>
                        (pede texto)
                      </Box>
                    )}
                  </>
                }
              />
            );
          })}
        </Box>
      )}
      <TextField
        sx={{ mt: 1.5 }}
        fullWidth
        multiline
        minRows={3}
        label={textRequired ? "Detalhe *" : "Detalhe (opcional)"}
        value={text}
        onChange={(e) => setText(e.target.value)}
        error={showError}
        inputProps={{ maxLength: TEXT_MAX, "aria-required": textRequired }}
        helperText={helper.replace(/\s+/g, " ").trim()}
      />
    </Box>
  );
}
